import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

export interface TripleAreaChartDataPoint {
  /** The x-axis value (typically position value) */
  xValue: number
  /** Vault (FOR) position value */
  vault?: number
  /** Counter vault (AGAINST) position value */
  counterVault?: number
}

export interface TripleAreaChartProps {
  /** Pre-formatted chart data points */
  chartData: TripleAreaChartDataPoint[]
  /** Display variant - 'min' hides axes/grid, 'max' shows full chart */
  variant?: 'min' | 'max'
  /** Color for vault (FOR) area */
  vaultColor?: string
  /** Color for counter vault (AGAINST) area */
  counterVaultColor?: string
}

/**
 * An area chart for visualizing triple position distributions.
 * Shows vault (FOR) positions on positive side and counter vault (AGAINST) on negative.
 * 
 * @example
 * <TripleAreaChart 
 *   chartData={formattedPositions} 
 *   variant="min" 
 * />
 */
export const TripleAreaChart = ({
  chartData,
  variant = 'min',
  vaultColor = 'green',
  counterVaultColor = 'red',
}: TripleAreaChartProps) => {
  // Clone data to avoid mutating props
  const data = [...chartData]
  
  const maxValue = Math.max(...data.map((d) => d.xValue))
  const minValue = Math.min(...data.map((d) => d.xValue))
  
  // Add padding points for better visualization
  if (maxValue > 0) {
    data.push({
      xValue: maxValue * 1.2,
      vault: maxValue,
    })
  }
  if (minValue < 0) {
    data.push({
      xValue: minValue * 1.2,
      counterVault: minValue * -1,
    })
  }
  
  data.sort((a, b) => a.xValue - b.xValue)
  const maxAbsValue = Math.max(...data.map((d) => Math.abs(d.xValue)))
  const domain = [maxAbsValue * -1, maxAbsValue]

  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} layout="vertical">
        {variant !== 'min' && (
          <>
            <CartesianGrid strokeDasharray="3 3" />
            <Tooltip />
          </>
        )}
        <XAxis type="number" hide={variant === 'min'} />
        <YAxis
          dataKey="xValue"
          type="number"
          reversed
          domain={domain}
          hide={variant === 'min'}
        />
        <Area
          type="stepBefore"
          dataKey="vault"
          stroke={vaultColor}
          fill={vaultColor}
          fillOpacity={0.5}
        />
        <Area
          type="stepAfter"
          dataKey="counterVault"
          stroke={counterVaultColor}
          fill={counterVaultColor}
          fillOpacity={0.5}
        />
      </AreaChart>
    </ResponsiveContainer>
  )
}

export default TripleAreaChart

